import React from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import {
  ORGANIZATION_TYPES,
  CARE_SETTINGS,
  BILLING_TYPES,
  RCM_DEPARTMENTS,
  PAYER_FOCUS,
  INTEREST_AREAS,
  ROLE_LEVELS,
  US_STATES,
  MAC_REGIONS,
} from './profileConstants';

export interface ProfileFormData {
  full_name: string;
  organization: string;
  organization_type: string;
  title: string;
}

export interface PreferencesFormData {
  care_settings: string[];
  billing_types: string[];
  rcm_departments: string[];
  payer_focus: string[];
  interest_areas: string[];
  role_level: string;
  primary_state: string;
  mac_region: string;
  additional_states: string[];
}

interface CheckboxGroupProps {
  label: string;
  description?: string;
  options: readonly string[];
  selected: string[];
  onChange: (selected: string[]) => void;
}

function CheckboxGroup({ label, description, options, selected, onChange }: CheckboxGroupProps) {
  const toggle = (option: string) => {
    if (selected.includes(option)) {
      onChange(selected.filter((s) => s !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold">{label}</h3>
        {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {options.map((option) => (
          <div key={option} className="flex items-start gap-2">
            <Checkbox
              id={`${label}-${option}`}
              checked={selected.includes(option)}
              onCheckedChange={() => toggle(option)}
              className="mt-0.5 shrink-0"
            />
            <label
              htmlFor={`${label}-${option}`}
              className="text-sm leading-snug cursor-pointer select-none"
            >
              {option}
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}

interface ProfilePreferencesFormProps {
  profile: ProfileFormData;
  onProfileChange: (data: ProfileFormData) => void;
  preferences: PreferencesFormData;
  onPreferencesChange: (data: PreferencesFormData) => void;
  onSubmit: (e: React.FormEvent) => void;
  saving: boolean;
  message: { type: 'success' | 'error'; text: string } | null;
  submitLabel: string;
  savingLabel: string;
  /** Show a skip/cancel action alongside submit */
  secondaryAction?: React.ReactNode;
}

export function ProfilePreferencesForm({
  profile,
  onProfileChange,
  preferences,
  onPreferencesChange,
  onSubmit,
  saving,
  message,
  submitLabel,
  savingLabel,
  secondaryAction,
}: ProfilePreferencesFormProps) {
  const updateProfile = (field: keyof ProfileFormData, value: string) =>
    onProfileChange({ ...profile, [field]: value });

  const updatePrefs = (field: keyof PreferencesFormData, value: string | string[]) =>
    onPreferencesChange({ ...preferences, [field]: value });

  return (
    <form onSubmit={onSubmit} className="space-y-8">
      {/* ── Profile Setup ── */}
      <section className="space-y-4">
        <h2 className="text-base font-semibold border-b pb-2">Profile Setup</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="fullName">Name</Label>
            <Input
              id="fullName"
              value={profile.full_name}
              onChange={(e) => updateProfile('full_name', e.target.value)}
              placeholder="Your full name"
              className="h-10"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">
              Title <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Input
              id="title"
              value={profile.title}
              onChange={(e) => updateProfile('title', e.target.value)}
              placeholder="e.g. Director of Revenue Cycle"
              className="h-10"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="organization">Company / Organization</Label>
            <Input
              id="organization"
              value={profile.organization}
              onChange={(e) => updateProfile('organization', e.target.value)}
              placeholder="Your organization"
              className="h-10"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="organizationType">Organization Type</Label>
            <Select
              value={profile.organization_type}
              onValueChange={(v) => updateProfile('organization_type', v)}
            >
              <SelectTrigger id="organizationType" className="h-10">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {ORGANIZATION_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* ── Profile Preferences ── */}
      <section className="space-y-6">
        <h2 className="text-base font-semibold border-b pb-2">Profile Preferences</h2>

        <CheckboxGroup
          label="1. Care Setting"
          description="Where you work or your areas of interest"
          options={CARE_SETTINGS}
          selected={preferences.care_settings}
          onChange={(v) => updatePrefs('care_settings', v)}
        />

        <CheckboxGroup
          label="2. Billing Type"
          options={BILLING_TYPES}
          selected={preferences.billing_types}
          onChange={(v) => updatePrefs('billing_types', v)}
        />

        <CheckboxGroup
          label="3. Revenue Cycle Department"
          options={RCM_DEPARTMENTS}
          selected={preferences.rcm_departments}
          onChange={(v) => updatePrefs('rcm_departments', v)}
        />

        <CheckboxGroup
          label="4. Payer Focus"
          options={PAYER_FOCUS}
          selected={preferences.payer_focus}
          onChange={(v) => updatePrefs('payer_focus', v)}
        />

        <CheckboxGroup
          label="5. Interest Areas"
          description="Used to personalize your content feed"
          options={INTEREST_AREAS}
          selected={preferences.interest_areas}
          onChange={(v) => updatePrefs('interest_areas', v)}
        />

        {/* Role Level — single select rendered as radio-style select */}
        <div className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold">6. Role Level</h3>
          </div>
          <Select
            value={preferences.role_level}
            onValueChange={(v) => updatePrefs('role_level', v)}
          >
            <SelectTrigger className="h-10 w-full sm:w-64">
              <SelectValue placeholder="Select your role level" />
            </SelectTrigger>
            <SelectContent>
              {ROLE_LEVELS.map((r) => (
                <SelectItem key={r} value={r}>
                  {r}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Geography */}
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold">7. Geography</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Determines regional policy updates, payer rules, and regulatory content shown to you.
              Many healthcare reimbursement policies vary by state and MAC region.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="primaryState">Primary State</Label>
              <Select
                value={preferences.primary_state}
                onValueChange={(v) => updatePrefs('primary_state', v)}
              >
                <SelectTrigger id="primaryState" className="h-10">
                  <SelectValue placeholder="Select primary state" />
                </SelectTrigger>
                <SelectContent>
                  {US_STATES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="macRegion">MAC Region</Label>
              <Select
                value={preferences.mac_region}
                onValueChange={(v) => updatePrefs('mac_region', v)}
              >
                <SelectTrigger id="macRegion" className="h-10">
                  <SelectValue placeholder="Select MAC region" />
                </SelectTrigger>
                <SelectContent>
                  {MAC_REGIONS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <Label className="text-sm font-medium">Multi-State Coverage</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Select any additional states you operate in or want to monitor.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-y-auto border rounded-md p-3">
              {US_STATES.map((state) => (
                <div key={state} className="flex items-center gap-2">
                  <Checkbox
                    id={`state-${state}`}
                    checked={preferences.additional_states.includes(state)}
                    onCheckedChange={() => {
                      const current = preferences.additional_states;
                      updatePrefs(
                        'additional_states',
                        current.includes(state)
                          ? current.filter((s) => s !== state)
                          : [...current, state]
                      );
                    }}
                    className="shrink-0"
                  />
                  <label
                    htmlFor={`state-${state}`}
                    className="text-sm leading-snug cursor-pointer select-none"
                  >
                    {state}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {message && (
        <Alert variant={message.type === 'success' ? 'success' : 'destructive'}>
          <AlertDescription>{message.text}</AlertDescription>
        </Alert>
      )}

      <div className="flex items-center gap-3 justify-end pt-2">
        {secondaryAction}
        <Button type="submit" disabled={saving}>
          {saving ? savingLabel : submitLabel}
        </Button>
      </div>
    </form>
  );
}
